/**
 * useMembership — Fetch current user's daily AI usage and tier info.
 *
 * GET /api/membership/usage → { tier, date, usage: { a2a: {used,limit}, ... } }
 * GET /api/membership/status → { membership: { tier, status, expires_at, ... } }
 */
import { useState, useCallback, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { get } from '../lib/api/client'
import { useAuth } from '../contexts/AuthContext'

export type MembershipTier = 'free' | 'starter' | 'pro' | 'business'

export interface UsageItem {
  used: number
  limit: number
}

export interface MembershipUsage {
  tier: MembershipTier
  date: string
  usage: Record<string, UsageItem>
}

// ─── Status hook (TanStack Query — auto-deduped across all components) ───────

export interface MembershipStatus {
  tier: MembershipTier
  status: string | null
  expires_at: string | null
  is_premium_active: boolean
}

export const membershipStatusKey = ['membership', 'status'] as const

export function useMembershipStatus() {
  const { user } = useAuth()
  return useQuery<MembershipStatus>({
    queryKey: membershipStatusKey,
    queryFn: async () => {
      const res = await get<{
        membership: {
          tier: string
          status: string | null
          expires_at: string | null
          is_premium_active: boolean
        }
      }>('/api/membership/status')
      const m = res.membership
      return {
        tier: (m.tier as MembershipTier) || 'free',
        status: m.status,
        expires_at: m.expires_at,
        is_premium_active: m.is_premium_active,
      }
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // membership rarely changes — 5 min cache
    gcTime: 10 * 60 * 1000,
  })
}

export function useMembership() {
  const { user } = useAuth()
  const [data, setData] = useState<MembershipUsage | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const midnightTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetch = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setError(null)
    try {
      const res = await get<MembershipUsage>('/api/membership/usage')
      setData(res)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [user])

  // Auto-fetch on mount / user change
  useEffect(() => {
    fetch()
  }, [fetch])

  // Schedule refetch at next midnight AEST (UTC+11)
  useEffect(() => {
    if (!user) return

    const scheduleNextMidnight = () => {
      const now = new Date()
      // AEST is UTC+11 (simplified — doesn't handle DST precisely but close enough)
      const aestOffset = 11 * 60
      const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes()
      const aestMinutes = (utcMinutes + aestOffset) % (24 * 60)
      const msUntilMidnight = ((24 * 60 - aestMinutes) % (24 * 60)) * 60 * 1000
      // Add 60s buffer so we're safely past midnight
      const delay = msUntilMidnight + 60_000

      midnightTimer.current = setTimeout(() => {
        fetch()
        scheduleNextMidnight()
      }, delay)
    }

    scheduleNextMidnight()
    return () => {
      if (midnightTimer.current) clearTimeout(midnightTimer.current)
    }
  }, [user, fetch])

  return { data, loading, error, refetch: fetch }
}
